import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { getDataProvider, subscribeToTable, type Subscription, type ChangePayload } from '@/lib/dataProvider'
import { useAuthStore } from './auth'
import { useScenesStore } from './scenes'
import { useDisplaysStore } from './displays'

// Layer and element types for display editor
export type LayerType = 'image' | 'video' | 'youtube' | 'text'

// Interface for image library items
export interface ImageLibraryItem {
  id: string
  user_id: string
  filename: string
  original_name: string
  file_size: number
  mime_type: string
  storage_path: string
  preview_storage_path?: string | null
  width?: number
  height?: number
  tags: string[]
  uploaded_at: string
  updated_at: string
  // Computed properties for display
  url?: string
  preview_url?: string
}

export interface LayerEffect {
  rotation?: number
  scaleX?: number
  scaleY?: number
  opacity?: number
  blendMode?: string
  hue?: number
  tint?: string
  saturation?: number
  brightness?: number
  contrast?: number
  blur?: number
  crop?: {
    x: number
    y: number
    width: number
    height: number
  }
  animation?: {
    type: 'shake' | 'scroll' | 'pulse' | 'bounce' | 'rotate'
    enabled: boolean
    speed?: number
    amplitude?: number
  }
}

// Interface for display layer overrides
export interface DisplayLayerOverride {
  id: string
  scene_display_config_id: string
  parent_layer_id: string
  visible?: boolean | null
  position_x?: number | null
  position_y?: number | null
  width?: number | null
  height?: number | null
  z_index?: number | null
  crop_settings?: {
    top: number
    left: number
    right: number
    bottom: number
  } | null
  effects?: LayerEffect | null
  created_at: string
  updated_at: string
}

// Updated DisplayLayer interface to match database schema
export interface DisplayLayer {
  id: string
  scene_display_config_id: string
  layer_type: LayerType
  name: string
  content_source: string // image_library.id for images, URL for youtube, text for text layers
  position_x: number
  position_y: number
  width: number
  height: number
  z_index: number
  visible: boolean
  locked: boolean
  object_fit: 'cover' | 'contain' | 'fill' | 'scale-down' | 'none' | 'fixed-aspect'
  crop_settings: {
    top: number
    left: number
    right: number
    bottom: number
  }
  effects: LayerEffect
  created_at: string
  updated_at: string
  
  // Layer inheritance fields
  inherited_from_scene_id?: string | null // If set, this layer is inherited from a parent scene
  overrides_layer_id?: string | null // If set, this layer overrides an inherited layer
  
  // Computed properties for backward compatibility and display
  type: LayerType // Alias for layer_type
  content: string // Resolved content URL
  storage_path?: string // For image layers: storage path for cache-first loading
  x: number // Alias for position_x
  y: number // Alias for position_y
  zIndex: number // Alias for z_index
  crop: {
    top: number
    left: number
    right: number
    bottom: number
  } // Alias for crop_settings
  objectFit: 'cover' | 'contain' | 'fill' | 'scale-down' | 'none' | 'fixed-aspect' // Alias for object_fit
  
  // Computed inheritance metadata
  is_inherited?: boolean // True if inherited_from_scene_id is set
  can_modify?: boolean // False if inherited (read-only from parent)
  parent_scene_name?: string // Name of the parent scene this was inherited from
  has_overrides?: boolean // True if this inherited layer has selective overrides applied
}

export const defaultLayerCrop = Object.freeze({
  top: 0,
  left: 0,
  right: 0,
  bottom: 0
})

export const normalizeDisplayLayer = (layer: DisplayLayer): DisplayLayer => {
  const cropSource = layer.crop ?? layer.crop_settings ?? defaultLayerCrop
  const normalizedCrop = {
    top: cropSource.top ?? defaultLayerCrop.top,
    left: cropSource.left ?? defaultLayerCrop.left,
    right: cropSource.right ?? defaultLayerCrop.right,
    bottom: cropSource.bottom ?? defaultLayerCrop.bottom
  }

  const effectsSource: LayerEffect = layer.effects ? { ...layer.effects } : {}
  const animation = effectsSource.animation ? { ...effectsSource.animation } : undefined

  return {
    ...layer,
    type: layer.type ?? layer.layer_type,
    content: layer.content ?? '',
    position_x: layer.position_x ?? layer.x ?? 0,
    position_y: layer.position_y ?? layer.y ?? 0,
    x: layer.x ?? layer.position_x ?? 0,
    y: layer.y ?? layer.position_y ?? 0,
    z_index: layer.z_index ?? layer.zIndex ?? 0,
    zIndex: layer.zIndex ?? layer.z_index ?? 0,
    crop_settings: { ...normalizedCrop },
    crop: { ...normalizedCrop },
    object_fit: layer.object_fit ?? layer.objectFit ?? 'cover',
    objectFit: layer.objectFit ?? layer.object_fit ?? 'cover',
    effects: {
      ...effectsSource,
      animation
    }
  }
}

export const cloneDisplayLayer = (layer: DisplayLayer): DisplayLayer => {
  const normalized = normalizeDisplayLayer(layer)
  return {
    ...normalized,
    crop: { ...normalized.crop },
    crop_settings: { ...normalized.crop_settings },
    effects: {
      ...normalized.effects,
      animation: normalized.effects.animation
        ? { ...normalized.effects.animation }
        : undefined
    }
  }
}

// Lightweight clone for read-only display purposes - avoids expensive normalization
// Use this when you just need to prevent mutations, not when you need normalized data
export const shallowCloneDisplayLayer = (layer: DisplayLayer): DisplayLayer => {
  return {
    ...layer,
    // Only clone objects that might be mutated
    crop: layer.crop ? { ...layer.crop } : defaultLayerCrop,
    crop_settings: layer.crop_settings ? { ...layer.crop_settings } : defaultLayerCrop,
    effects: layer.effects ? {
      ...layer.effects,
      animation: layer.effects.animation ? { ...layer.effects.animation } : undefined
    } : {}
  }
}

export const displayLayerToUpdatePayload = (layer: DisplayLayer): UpdateDisplayLayerData => {
  const normalized = normalizeDisplayLayer(layer)
  const effects = normalized.effects
    ? {
        ...normalized.effects,
        animation: normalized.effects.animation
          ? { ...normalized.effects.animation }
          : undefined
      }
    : undefined

  return {
    name: normalized.name,
    content_source: normalized.content_source,
    position_x: normalized.x,
    position_y: normalized.y,
    width: normalized.width,
    height: normalized.height,
    z_index: normalized.zIndex,
    visible: normalized.visible,
    locked: normalized.locked,
    object_fit: normalized.objectFit,
    crop_settings: { ...normalized.crop },
    effects
  }
}

const cloneConfigLayers = (layers?: DisplayLayer[]) => {
  return layers ? layers.map(cloneDisplayLayer) : layers
}

// Interface for creating new display layers
export interface CreateDisplayLayerData {
  scene_display_config_id: string
  layer_type: LayerType
  name: string
  content_source: string
  position_x?: number
  position_y?: number
  width?: number
  height?: number
  z_index?: number
  visible?: boolean
  locked?: boolean
  object_fit?: 'cover' | 'contain' | 'fill' | 'scale-down' | 'none' | 'fixed-aspect'
  crop_settings?: {
    top: number
    left: number
    right: number
    bottom: number
  }
  effects?: LayerEffect
  // Layer inheritance fields
  inherited_from_scene_id?: string | null
  overrides_layer_id?: string | null
}

// Interface for creating new image library items
export interface CreateImageLibraryData {
  filename: string
  original_name: string
  file_size: number
  mime_type: string
  storage_path: string
  preview_storage_path?: string
  width?: number
  height?: number
  tags?: string[]
}

// Interface for updating display layers
export interface UpdateDisplayLayerData {
  name?: string
  content_source?: string
  position_x?: number
  position_y?: number
  width?: number
  height?: number
  z_index?: number
  visible?: boolean
  locked?: boolean
  object_fit?: 'cover' | 'contain' | 'fill' | 'scale-down' | 'none' | 'fixed-aspect'
  crop_settings?: {
    top: number
    left: number
    right: number
    bottom: number
  }
  effects?: LayerEffect
}



// TypeScript interfaces based on the database schema
export interface SceneDisplayConfig {
  id: string
  scene_id: string
  display_id: string
  image_url: string | null
  audio_url: string | null
  volume: number
  loop_audio: boolean
  audio_fade: number
  preview_storage_path: string | null // Cached preview image path (400px)
  layers?: DisplayLayer[] // New field for layers
  created_at: string
  updated_at: string
}

export interface CreateSceneDisplayConfigData {
  scene_id: string
  display_id: string
  image_url?: string | null
  audio_url?: string | null
  volume?: number
  loop_audio?: boolean
  audio_fade?: number
  layers?: DisplayLayer[]
}

export interface UpdateSceneDisplayConfigData {
  image_url?: string | null
  audio_url?: string | null
  volume?: number
  loop_audio?: boolean
  audio_fade?: number
  layers?: DisplayLayer[]
}

// Resolved config for display purposes with inheritance info
export interface ResolvedSceneDisplayConfig {
  scene_id: string
  display_id: string
  // Current values (either direct or inherited)
  image_url: string | null
  audio_url: string | null
  volume: number
  loop_audio: boolean
  audio_fade: number
  layers: DisplayLayer[]
  // Inheritance tracking
  has_direct_config: boolean
  image_url_inherited_from: string | null // scene name
  audio_config_inherited_from: string | null // scene name
  layers_inherited_from: string | null // scene name
  // Direct config (if exists)
  direct_config: SceneDisplayConfig | null
}

export const useSceneDisplayConfigsStore = defineStore('sceneDisplayConfigs', () => {
  // State
  const configs = ref<Map<string, SceneDisplayConfig>>(new Map())
  const resolvedConfigs = ref<Map<string, ResolvedSceneDisplayConfig>>(new Map())
  const loadingStates = ref<Map<string, boolean>>(new Map())
  const error = ref<string | null>(null)
  const initialized = ref(false)
  const initializationPromise = ref<Promise<void> | null>(null)

  // Getters
  const getConfigsForScene = computed(() => (sceneId: string) => {
    return Array.from(configs.value.values())
      .filter(config => config.scene_id === sceneId)
  })

  const getResolvedConfigsForScene = computed(() => (sceneId: string) => {
    return Array.from(resolvedConfigs.value.values())
      .filter(config => config.scene_id === sceneId)
  })

  const getConfigForSceneAndDisplay = computed(() => (sceneId: string, displayId: string) => {
    return Array.from(configs.value.values())
      .find(config => config.scene_id === sceneId && config.display_id === displayId)
  })

  const getResolvedConfigForSceneAndDisplay = computed(() => (sceneId: string, displayId: string) => {
    return Array.from(resolvedConfigs.value.values())
      .find(config => config.scene_id === sceneId && config.display_id === displayId)
  })

  const isLoading = computed(() => (key: string) => {
    return loadingStates.value.get(key) || false
  })

  // Helper functions
  const setLoading = (key: string, loading: boolean) => {
    loadingStates.value.set(key, loading)
  }

  const setError = (errorMessage: string | null) => {
    error.value = errorMessage
  }

  const addConfig = (config: SceneDisplayConfig) => {
    const key = `${config.scene_id}-${config.display_id}`
    configs.value.set(key, {
      ...config,
      layers: cloneConfigLayers(config.layers)
    })
  }

  const removeConfig = (sceneId: string, displayId: string) => {
    const key = `${sceneId}-${displayId}`
    configs.value.delete(key)
    resolvedConfigs.value.delete(key)
  }

  const updateConfig = (sceneId: string, displayId: string, updates: Partial<SceneDisplayConfig>) => {
    const key = `${sceneId}-${displayId}`
    const config = configs.value.get(key)
    if (config) {
      configs.value.set(key, {
        ...config,
        ...updates,
        layers: updates.layers !== undefined ? cloneConfigLayers(updates.layers) : config.layers
      })
    }
  }

  // Inheritance resolution - traverse scene hierarchy to resolve configs
  const resolveConfigInheritance = async (sceneId: string, displayId: string): Promise<ResolvedSceneDisplayConfig | null> => {
    const scenesStore = useScenesStore()
    const scene = scenesStore.scenes.get(sceneId)
    if (!scene) return null

    // Get direct config for this scene
    const directConfig = getConfigForSceneAndDisplay.value(sceneId, displayId)
    
    // Start with defaults
    const resolvedConfig: ResolvedSceneDisplayConfig = {
      scene_id: sceneId,
      display_id: displayId,
      image_url: null,
      audio_url: null,
      volume: 0.5,
      loop_audio: true,
      audio_fade: 10.0,
      layers: [],
      has_direct_config: !!directConfig,
      image_url_inherited_from: null,
      audio_config_inherited_from: null,
      layers_inherited_from: null,
      direct_config: directConfig || null
    }

    // If we have a direct config, use its non-null values
    if (directConfig) {
      if (directConfig.image_url !== null) {
        resolvedConfig.image_url = directConfig.image_url
      }
      if (directConfig.audio_url !== null) {
        resolvedConfig.audio_url = directConfig.audio_url
        resolvedConfig.volume = directConfig.volume
        resolvedConfig.loop_audio = directConfig.loop_audio
        resolvedConfig.audio_fade = directConfig.audio_fade
      }
      if (directConfig.layers && directConfig.layers.length > 0) {
        resolvedConfig.layers = cloneConfigLayers(directConfig.layers) ?? []
      }
    }

    // Traverse up the scene hierarchy to find inherited values for null fields
    let currentScene = scene
    while (currentScene.parent_id) {
      const parentScene = scenesStore.scenes.get(currentScene.parent_id)
      if (!parentScene) break

      const parentConfig = getConfigForSceneAndDisplay.value(parentScene.id, displayId)
      if (parentConfig) {
        // Inherit image_url if current scene doesn't have it set (null or no direct config)
        if (parentConfig.image_url && resolvedConfig.image_url === null) {
          resolvedConfig.image_url = parentConfig.image_url
          resolvedConfig.image_url_inherited_from = parentScene.name
        }

        // Inherit audio config if current scene doesn't have it set
        if (parentConfig.audio_url && resolvedConfig.audio_url === null) {
          resolvedConfig.audio_url = parentConfig.audio_url
          resolvedConfig.volume = parentConfig.volume
          resolvedConfig.loop_audio = parentConfig.loop_audio
          resolvedConfig.audio_fade = parentConfig.audio_fade
          resolvedConfig.audio_config_inherited_from = parentScene.name
        }

        // Inherit layers if current scene doesn't have any
        if (parentConfig.layers && parentConfig.layers.length > 0 && resolvedConfig.layers.length === 0) {
          resolvedConfig.layers = cloneConfigLayers(parentConfig.layers) ?? []
          resolvedConfig.layers_inherited_from = parentScene.name
        }

        // If we found both image and audio, we're done
        if (resolvedConfig.image_url !== null && resolvedConfig.audio_url !== null) {
          break
        }
      }

      currentScene = parentScene
    }

    return resolvedConfig
  }

  // Actions
  const fetchAllConfigs = async () => {
    const authStore = useAuthStore()
    if (!authStore.user) return

    if (initialized.value) return
    if (initializationPromise.value) return initializationPromise.value

    const loadingKey = 'fetchAllConfigs'
    setLoading(loadingKey, true)
    
    initializationPromise.value = (async () => {
      try {
        const provider = getDataProvider()
        const data = await provider.query<SceneDisplayConfig>('scene_display_configs')

        // Clear and rebuild
        configs.value.clear()
        data?.forEach(config => addConfig(config))
        
        initialized.value = true
        console.log('✅ [sceneDisplayConfigs] Fetched all configs', { count: data?.length })
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch all configs'
        setError(errorMessage)
        console.error('❌ [sceneDisplayConfigs] Failed to fetch all configs:', err)
        throw err
      } finally {
        setLoading(loadingKey, false)
        initializationPromise.value = null
      }
    })()

    return initializationPromise.value
  }

  const fetchConfigsForScene = async (sceneId: string) => {
    // If we've already initialized everything, we don't need to fetch specific scenes
    if (initialized.value) return { success: true }
    
    // If initialization is in progress, wait for it
    if (initializationPromise.value) {
      await initializationPromise.value
      return { success: true }
    }

    const authStore = useAuthStore()
    if (!authStore.user) {
      setError('User not authenticated')
      return { success: false, error: 'User not authenticated' }
    }

    const loadingKey = `fetchConfigs-${sceneId}`
    setLoading(loadingKey, true)
    setError(null)

    try {
      const provider = getDataProvider()
      const data = await provider.query<SceneDisplayConfig>('scene_display_configs', {
        filters: [{ column: 'scene_id', operator: 'eq', value: sceneId }]
      })

      // Add to local state
      data?.forEach(config => addConfig(config))

      return { success: true, data }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch scene display configs'
      setError(errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      setLoading(loadingKey, false)
    }
  }

  const fetchResolvedConfigsForScene = async (sceneId: string) => {
    // First ensure we have the configs loaded
    await fetchConfigsForScene(sceneId)
    
    // Then resolve inheritance for each display
    const displaysStore = useDisplaysStore()
    
    // Clear existing resolved configs for this scene
    Array.from(resolvedConfigs.value.keys())
      .filter(key => key.startsWith(`${sceneId}-`))
      .forEach(key => resolvedConfigs.value.delete(key))

    // Resolve configs for all displays
    for (const display of displaysStore.allDisplays) {
      const resolved = await resolveConfigInheritance(sceneId, display.id)
      if (resolved) {
        const key = `${sceneId}-${display.id}`
        resolvedConfigs.value.set(key, resolved)
      }
    }

    return { success: true }
  }

  const createOrUpdateConfig = async (data: CreateSceneDisplayConfigData) => {
    const authStore = useAuthStore()
    if (!authStore.user) return { success: false, error: 'User not authenticated' }

    const loadingKey = `saveConfig-${data.scene_id}-${data.display_id}`
    setLoading(loadingKey, true)
    setError(null)

    try {
      const provider = getDataProvider()
      
      // Check if config already exists
      const existingConfig = getConfigForSceneAndDisplay.value(data.scene_id, data.display_id)

      let result
      if (existingConfig) {
        // Update existing config
        const updateResult = await provider.update<SceneDisplayConfig>('scene_display_configs', existingConfig.id, {
          image_url: data.image_url,
          audio_url: data.audio_url,
          volume: data.volume ?? 0.5,
          loop_audio: data.loop_audio ?? true,
          audio_fade: data.audio_fade ?? 10.0
        })

        if (!updateResult.success) throw new Error(updateResult.error)
        result = updateResult.data
      } else {
        // Create new config
        const insertResult = await provider.insert<SceneDisplayConfig>('scene_display_configs', {
          scene_id: data.scene_id,
          display_id: data.display_id,
          image_url: data.image_url,
          audio_url: data.audio_url,
          volume: data.volume ?? 0.5,
          loop_audio: data.loop_audio ?? true,
          audio_fade: data.audio_fade ?? 10.0
        })

        if (!insertResult.success) throw new Error(insertResult.error)
        result = insertResult.data
      }

      if (!result) throw new Error('No data returned from config save')

      // Update local state
      addConfig(result)
      
      // Re-resolve configs for this scene
      await fetchResolvedConfigsForScene(data.scene_id)

      return { success: true, data: result }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save scene display config'
      setError(errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      setLoading(loadingKey, false)
    }
  }

  const resetFieldToParent = async (sceneId: string, displayId: string, fieldGroup: 'image' | 'audio') => {
    const config = getConfigForSceneAndDisplay.value(sceneId, displayId)
    if (!config) return { success: false, error: 'Config not found' }

    // Prepare updates to reset specific field group to null (inherit from parent)
    const updates: UpdateSceneDisplayConfigData = {}
    
    if (fieldGroup === 'image') {
      updates.image_url = null
    } else {
      updates.audio_url = null
      updates.volume = 0.5
      updates.loop_audio = true
      updates.audio_fade = 10.0
    }

    // Check if all fields would be null after this update
    const wouldAllBeNull = fieldGroup === 'image' 
      ? config.audio_url === null && config.volume === 0.5 && config.loop_audio === true && config.audio_fade === 10.0
      : config.image_url === null

    if (wouldAllBeNull) {
      // Delete the entire config record since all fields would be defaults/null
      return await deleteConfig(sceneId, displayId)
    } else {
      // Update the config with reset fields
      return await createOrUpdateConfig({
        scene_id: sceneId,
        display_id: displayId,
        ...updates
      })
    }
  }

  const deleteConfig = async (sceneId: string, displayId: string) => {
    const config = getConfigForSceneAndDisplay.value(sceneId, displayId)
    if (!config) return { success: false, error: 'Config not found' }

    setLoading(`deleteConfig-${sceneId}-${displayId}`, true)
    setError(null)

    try {
      const provider = getDataProvider()
      const result = await provider.delete('scene_display_configs', config.id)

      if (!result.success) throw new Error(result.error)

      // Remove from local state
      removeConfig(sceneId, displayId)

      return { success: true }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete scene display config'
      setError(errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      setLoading(`deleteConfig-${sceneId}-${displayId}`, false)
    }
  }

  const subscribeToConfigUpdates = (): Subscription | null => {
    const authStore = useAuthStore()
    if (!authStore.user) return null

    return subscribeToTable<SceneDisplayConfig>(
      'scene_display_configs',
      (payload: ChangePayload<SceneDisplayConfig>) => {
        handleConfigChange(payload)
      }
    )
  }

  const handleConfigChange = (payload: ChangePayload<SceneDisplayConfig>) => {
    const { eventType, new: newRecord, old: oldRecord } = payload

    switch (eventType) {
      case 'INSERT':
        if (newRecord) {
          addConfig(newRecord)
          // Re-resolve configs for affected scene
          fetchResolvedConfigsForScene(newRecord.scene_id)
        }
        break
      
      case 'UPDATE':
        if (newRecord) {
          updateConfig(newRecord.scene_id, newRecord.display_id, newRecord)
          // Re-resolve configs for affected scene
          fetchResolvedConfigsForScene(newRecord.scene_id)
        }
        break
      
      case 'DELETE':
        if (oldRecord && oldRecord.scene_id && oldRecord.display_id) {
          removeConfig(oldRecord.scene_id, oldRecord.display_id)
        }
        break
    }
  }

  const unsubscribeFromUpdates = (subscription: Subscription | null) => {
    if (subscription) {
      subscription.unsubscribe()
    }
  }

  return {
    // State
    configs,
    resolvedConfigs,
    error,
    
    // Getters
    getConfigsForScene,
    getResolvedConfigsForScene,
    getConfigForSceneAndDisplay,
    getResolvedConfigForSceneAndDisplay,
    isLoading,
    
    // Actions
    fetchConfigsForScene,
    fetchAllConfigs,
    fetchResolvedConfigsForScene,
    createOrUpdateConfig,
    resetFieldToParent,
    deleteConfig,
    subscribeToConfigUpdates,
    unsubscribeFromUpdates
  }
})